import { Component, AfterViewInit, Input, OnInit, OnDestroy } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { Store, select } from '@ngrx/store';
import { Observable, of, Subject } from 'rxjs';
import { filter, tap, map, takeUntil } from 'rxjs/operators';

import { NavbarItem } from '@common/models/navbar-item';
import * as fromRootStore from '@apps/root/store';
import * as fromAuthStore from '@apps/auth/store';
import { IAuroraState } from '@apps/aurora.state';
import { Its2Constants } from '@apps/its2/its2.constants';
import { RequestsFilterService } from '@apps/its2/services';
import { CmdbConstants } from '@apps/cmdb/cmdb.constants';
import { SecuConstants } from '@apps/secu/secu-constants';

@Component({
    selector: 'aa-navbar-item',
    templateUrl: './navbar-item.component.html',
    styleUrls: [
        './navbar-item.component.scss'
    ],
    standalone: false
})
export class NavbarItemComponent implements OnInit, AfterViewInit, OnDestroy
{
    @Input() navbarItem: NavbarItem;
    private destroyator = new Subject();
    public isActive: Observable<boolean> = of(false);
    public isAuthorized: Observable<boolean> = of(false);
    public value: Observable<number> = of(0);

    constructor (
        private store: Store<IAuroraState>,
        private translate: TranslateService,
        private routerStore: Store<any>,
        private requestsFilterService: RequestsFilterService

    )
    {
        this.translate.onLangChange
            .pipe(
                tap(() =>
                    this.setText(this.navbarItem.id)
                ),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }

    ngOnInit (): void
    {
        this.isAuthorized = this.store
            .pipe(
                select(fromAuthStore.getApiAuthorization, { application: this.navbarItem.api, role: null }),
                map((authorized: boolean) =>
                    this.navbarItem.habilitation ? this.navbarItem.enabled && authorized : this.navbarItem.enabled
                )
            );

        if (this.navbarItem.api === CmdbConstants.Application)
        {
            this.store.select( fromAuthStore.getApiAuthorization, { application: Its2Constants.Application, role: null })
                .pipe(
                    tap((its2Rights: boolean): void =>
                    {
                        this.setCmdbLink(its2Rights);
                    }),
                    takeUntil(this.destroyator)
                )
                .subscribe();
        }

        if (this.navbarItem.api === SecuConstants.Application)
        {
            this.store.select(fromAuthStore.isCatalogSecuAuthorized)
                .pipe(
                    tap((isAuthorized: boolean): void =>
                    {
                        this.setSecuLink(isAuthorized);
                    }),
                    takeUntil(this.destroyator)
                )
                .subscribe();
        }

        this.isActive = this.routerStore
            .pipe(
                select(fromRootStore.selectUrl),
                filter((url: string) => url && 0 !== url.length),
                map((url: string) => url.startsWith(this.navbarItem.activeWhen))
            );

        this.value = this.routerStore.select(fromRootStore.getWarningsValue(this.navbarItem.id));
    }

    ngAfterViewInit ()
    {
        Promise.resolve(null).then(() =>
            this.setText(this.navbarItem.id)
        );
    }

    ngOnDestroy ()
    {
        this.destroyator.next(null);
        this.destroyator.complete();
    }

    onClick ()
    {
        switch (this.navbarItem.id.toLowerCase())
        {
            case Its2Constants.BaseApi:
                this.requestsFilterService.resetFilter();
                break;

            default:
                break;
        }
    }

    private setText (id: string)
    {
        this.navbarItem.text = this.translate.instant(`LAYOUT.MENU.${id}`);
    }

    private setCmdbLink (its2Rights: boolean): void
    {
        this.navbarItem.link = its2Rights ?
            CmdbConstants.DefaultUrl :
            CmdbConstants.FallbackUrl;
    }

    private setSecuLink (isCatalogSecuAuthorized: boolean): void
    {
        this.navbarItem.link = isCatalogSecuAuthorized ?
            SecuConstants.DefaultUrl :
            SecuConstants.FallbackUrl;
    }
}
